import React, { useEffect, useState, useRef } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Checkbox,
  Radio,
  Upload,
  message,
  Row,
  Col,
  Divider,
  AutoComplete,
  Cascader,
  Button,
  Card,
  Space
} from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { createApartment, updateApartment, uploadFile } from '../../../../api/apartment';
import { getDistrictList, getEnum, getSubwayList } from '../../../../api/common';
import { getUserInfo } from '../../../../utils/auth';
import './index.css';

const { TextArea } = Input;
const { Option } = Select;

const ApartmentModal = ({ visible, record, onCancel, onSuccess, detailOnly = false }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [subwayOptions, setSubwayOptions] = useState([]); // 地铁站
  const [serviceOptions, setServiceOptions] = useState([]); // 公寓服务
  const [orientationOptions, setOrientationOptions] = useState([]); // 朝向
  const [kitchenOptions, setKitchenOptions] = useState([]); // 是否有厨房
  const [facilityOptions, setFacilityOptions] = useState([]); // 家具设施
  const [roomTypeOptions, setRoomTypeOptions] = useState([]); // 户型
  const [autoComplete, setAutoComplete] = useState(null); // 高德地图自动完成实例
  const [addressOptions, setAddressOptions] = useState([]); // 地址候选项
  
  // 房型文件列表状态：使用对象数组来存储每个房型的文件列表
  // 格式：[{ images: [], video: [] }, { images: [], video: [] }, ...]
  const [roomTypeFileLists, setRoomTypeFileLists] = useState([]);
  
  // 用于跟踪批量上传状态的 ref
  const uploadingMessagesRef = useRef({});

  // 获取行政区和地铁站列表
  const fetchOptions = async () => {
    try {
      // 获取行政区列表
      const districtResult = await getDistrictList();
      if (districtResult.code === 200 && districtResult.data) {
        const districtList = districtResult.data.map(item => ({
          label: item.name,
          value: item.code
        }));
        setDistrictOptions(districtList);
      }

      // 获取地铁站列表
      const subwayResult = await getSubwayList();
      if (subwayResult.code === 200 && subwayResult.data) {
        // 转换为 Cascader 需要的格式：{ label, value, children }
        const subwayList = subwayResult.data.map(line => ({
          label: line.lineName,
          value: line.lineCode,
          children: line.subwayList.map(station => ({
            label: station.subwayName,
            value: station.subwayCode
          }))
        }));
        setSubwayOptions(subwayList);
      }
    } catch (error) {
      console.error('获取行政区和地铁站列表失败:', error);
    }
  };

  // 获取枚举数据
  const fetchEnumData = async () => {
    try {
      // 并行获取所有枚举数据
      const [serviceResult, orientationResult, kitchenResult, facilityResult, roomTypeResult] = await Promise.all([
        getEnum(1), // 公寓服务
        getEnum(5), // 朝向
        getEnum(6), // 是否有厨房
        getEnum(2), // 家具设施
        getEnum(7)  // 户型
      ]);

      // 公寓服务
      if (serviceResult.code === 200 && serviceResult.data) {
        const services = serviceResult.data.map(item => ({
          label: item.name,
          value: item.code
        }));
        setServiceOptions(services);
      }

      // 朝向
      if (orientationResult.code === 200 && orientationResult.data) {
        const orientations = orientationResult.data.map(item => ({
          label: item.name,
          value: item.code
        }));
        setOrientationOptions(orientations);
      }

      // 是否有厨房
      if (kitchenResult.code === 200 && kitchenResult.data) {
        const kitchens = kitchenResult.data.map(item => ({
          label: item.name,
          value: item.code
        }));
        setKitchenOptions(kitchens);
      }

      // 家具设施
      if (facilityResult.code === 200 && facilityResult.data) {
        const facilities = facilityResult.data.map(item => ({
          label: item.name,
          value: item.code
        }));
        setFacilityOptions(facilities);
      }

      // 户型
      if (roomTypeResult.code === 200 && roomTypeResult.data) {
        const roomTypes = roomTypeResult.data.map(item => ({
          label: item.name,
          value: item.code
        }));
        setRoomTypeOptions(roomTypes);
      }
    } catch (error) {
      console.error('获取枚举数据失败:', error);
    }
  };

  // 根据地铁站代码查找线路代码
  const findSubwayLine = (subwayCode, subwayOptions) => {
    for (const line of subwayOptions) {
      if (line.children) {
        for (const station of line.children) {
          if (station.value === subwayCode) {
            return line.value; // 返回线路代码
          }
        }
      }
    }
    return null;
  };

  // 处理图片/视频 URL，如果是相对路径则拼接当前域名
  const getFullUrl = (url) => {
    if (!url) return url;
    // 如果是相对路径（以 / 开头），则拼接当前页面的协议和域名
    if (url.startsWith('/')) {
      // 使用 window.location.origin 获取当前的协议+域名+端口
      return `${window.location.origin}${url}`;
    }
    return url;
  };

  useEffect(() => {
    if (visible) {
      // 清空地址候选项
      setAddressOptions([]);
      
      // 先加载选项数据
      const loadData = async () => {
        await fetchOptions();
        await fetchEnumData();
      };
      loadData();
      
      // 初始化高德地图自动完成
      if (window.AMap) {
        const auto = new window.AMap.AutoComplete({
          city: '上海', // 限制城市为上海
        });
        setAutoComplete(auto);
      }
      
      if (record) {
        // 编辑模式，填充表单数据
        const formData = {
          name: record.houseName,
          district: record.districtAmapCode,
          contactPhone: record.contactPhone,
          description: record.description,
          coordinate: record.coordinate,
          amapId: record.amapId,
          address: record.mapName || record.address, // 优先使用 mapName
          mapName: record.mapName, // 保存 mapName 用于提交
          roomTypes: [] // 房型列表
        };
        
        // 解析 houseTag 数据
        if (record.houseTag && Array.isArray(record.houseTag)) {
          record.houseTag.forEach(tag => {
            switch (tag.tagType) {
              case "1": // 公寓服务
                formData.services = tag.tagList;
                break;
              case "6": // 是否有厨房
                formData.hasKitchen = tag.tagList && tag.tagList[0];
                break;
              case "2": // 家具设施
                formData.facilities = tag.tagList;
                break;
            }
          });
        }
        
        // 解析房型列表数据
        const fileLists = [];
        
        if (record.houseTypeList && Array.isArray(record.houseTypeList) && record.houseTypeList.length > 0) {
          // 新格式：从 houseTypeList 中读取
          record.houseTypeList.forEach((houseType) => {
            const roomTypeData = {
              typeName: houseType.typeName,
              roomType: houseType.layoutTagCode,
              roomArea: houseType.area,
              price: houseType.price,
              orientation: houseType.orientationCode
            };
            
            formData.roomTypes.push(roomTypeData);
            
            // 设置该房型的文件列表
            const fileList = {
              images: [],
              video: []
            };
            
            // 设置图片列表
            if (houseType.imageList && Array.isArray(houseType.imageList)) {
              fileList.images = houseType.imageList.map((url, index) => ({
                uid: `-image-${houseType.typeName}-${index}`,
                name: `image-${index}.jpg`,
                status: 'done',
                url: getFullUrl(url)
              }));
            }
            
            // 设置视频
            if (houseType.videoUrl) {
              fileList.video = [{
                uid: `-video-${houseType.typeName}`,
                name: 'video.mp4',
                status: 'done',
                url: getFullUrl(houseType.videoUrl)
              }];
            }
            
            fileLists.push(fileList);
          });
        } else {
          // 旧格式兼容：使用旧的数据格式作为第一个房型
          const roomTypeData = {
            typeName: record.houseName || '房型1', // 默认房型名称
            roomType: undefined,
            roomArea: record.area,
            price: record.price,
            orientation: undefined
          };
          
          // 解析房型相关的 houseTag
          if (record.houseTag && Array.isArray(record.houseTag)) {
            record.houseTag.forEach(tag => {
              switch (tag.tagType) {
                case "7": // 户型
                  roomTypeData.roomType = tag.tagList && tag.tagList[0];
                  break;
                case "5": // 朝向
                  roomTypeData.orientation = tag.tagList && tag.tagList[0];
                  break;
              }
            });
          }
          
          formData.roomTypes = [roomTypeData];
          
          // 设置房型文件列表
          const firstRoomType = {
            images: [],
            video: []
          };
          
          // 设置图片列表
          if (record.houseImages && Array.isArray(record.houseImages)) {
            firstRoomType.images = record.houseImages.map((url, index) => ({
              uid: `-image-${index}`,
              name: `image-${index}.jpg`,
              status: 'done',
              url: getFullUrl(url)
            }));
          }
          
          // 设置视频列表
          if (record.houseVideo) {
            firstRoomType.video = [{
              uid: '-video-1',
              name: 'video.mp4',
              status: 'done',
              url: getFullUrl(record.houseVideo)
            }];
          }
          
          fileLists.push(firstRoomType);
        }
        
        form.setFieldsValue(formData);
        setRoomTypeFileLists(fileLists);
      } else {
        // 新增模式，重置表单
        form.resetFields();
        // 初始化一个空的房型
        form.setFieldsValue({
          roomTypes: [{}]
        });
        setRoomTypeFileLists([{ images: [], video: [] }]);
        
        // 设置默认联系人手机号为当前用户手机号
        const userInfo = getUserInfo();
        if (userInfo && userInfo.phone) {
          form.setFieldsValue({
            contactPhone: userInfo.phone
          });
        }
      }
    } else {
      // 弹窗关闭时，清空地址候选项
      setAddressOptions([]);
    }
  }, [visible, record, form]);

  // 当 subwayOptions 加载完成后，设置地铁站的值
  useEffect(() => {
    if (visible && record && subwayOptions.length > 0 && record.subwayAmapCode) {
      const lineCode = findSubwayLine(record.subwayAmapCode, subwayOptions);
      if (lineCode) {
        form.setFieldsValue({
          subway: [lineCode, record.subwayAmapCode]
        });
      }
    }
  }, [visible, record, subwayOptions, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // 验证每个房型都至少上传一张图片
      const roomTypes = values.roomTypes || [];
      for (let i = 0; i < roomTypes.length; i++) {
        const fileList = roomTypeFileLists[i];
        if (!fileList || fileList.images.length === 0) {
          message.error(`请为第 ${i + 1} 个房型至少上传一张图片`);
          return;
        }
        // 检查图片是否都上传成功（有URL或response）
        const allImagesUploaded = fileList.images.every(
          file => file.url || file.response || file.status === 'done'
        );
        if (!allImagesUploaded) {
          message.error(`第 ${i + 1} 个房型还有图片正在上传中，请稍候`);
          return;
        }
      }
      
      setLoading(true);

      // 构建 houseTag 数组（公寓级别的标签）
      const houseTag = [];
      
      // 公寓服务（tagType: 1）
      if (values.services && values.services.length > 0) {
        houseTag.push({
          tagType: "1",
          tagList: values.services
        });
      }
      
      // 是否有厨房（tagType: 6）
      if (values.hasKitchen) {
        houseTag.push({
          tagType: "6",
          tagList: [values.hasKitchen]
        });
      }
      
      // 家具设施（tagType: 2）
      if (values.facilities && values.facilities.length > 0) {
        houseTag.push({
          tagType: "2",
          tagList: values.facilities
        });
      }

      // 构建 houseTypeList 数组
      const houseTypeList = [];
      roomTypes.forEach((roomType, index) => {
        const fileList = roomTypeFileLists[index];
        
        // 提取图片URL
        const imageList = fileList.images.map(file => {
          // 如果是编辑模式回显的图片，url 可能是完整URL，需要提取相对路径
          if (file.url && !file.response) {
            // 如果是完整URL，提取路径部分
            if (file.url.startsWith('http')) {
              const urlObj = new URL(file.url);
              return urlObj.pathname; // 返回相对路径，如 /upload/xxx.jpg
            }
            return file.url; // 已经是相对路径
          }
          // 如果是新上传的图片，使用response（上传接口返回的URL）
          return file.response || file.url;
        }).filter(url => url); // 过滤掉空值
        
        // 提取视频URL
        let videoUrl = '';
        if (fileList.video.length > 0) {
          const videoFile = fileList.video[0];
          // 同样处理视频URL
          if (videoFile.url && !videoFile.response) {
            // 如果是完整URL，提取路径部分
            if (videoFile.url.startsWith('http')) {
              const urlObj = new URL(videoFile.url);
              videoUrl = urlObj.pathname; // 返回相对路径
            } else {
              videoUrl = videoFile.url; // 已经是相对路径
            }
          } else {
            videoUrl = videoFile.response || videoFile.url;
          }
        }
        
        // 构建房型对象
        const houseTypeItem = {
          typeName: roomType.typeName, // 房型名称
          layoutTagCode: roomType.roomType, // 户型代码
          area: roomType.roomArea, // 面积
          price: roomType.price, // 价格
          orientationCode: roomType.orientation, // 朝向代码
          imageList: imageList, // 图片列表
          videoUrl: videoUrl // 视频URL
        };
        
        houseTypeList.push(houseTypeItem);
      });

      // 使用 FormData 构建提交数据
      const formData = new FormData();
      
      // 添加基本字段
      formData.append('houseName', values.name);
      formData.append('cityCode', '310000'); // 上海城市代码（固定值）
      formData.append('districtAmapCode', values.district);
      formData.append('subwayAmapCode', values.subway[1]); // 取地铁站代码（数组第二个值）
      formData.append('description', values.description);
      formData.append('coordinate', values.coordinate);
      formData.append('amapId', values.amapId);
      formData.append('mapName', values.mapName); // 地址名称
      formData.append('contactPhone', values.contactPhone); // 联系人手机号
      formData.append('vacantRoomCount', '0'); // 空房数量（固定值）
      formData.append('minLease', '0'); // 最短租期（固定值）
      
      // 添加 houseTag（转为 JSON 字符串）
      formData.append('houseTag', JSON.stringify(houseTag));
      
      // 添加 houseTypeList（转为 JSON 字符串）
      formData.append('houseTypeList', JSON.stringify(houseTypeList));

      if (record) {
        // 编辑模式：添加 id 参数
        formData.append('id', record.id);
        await updateApartment(formData);
        message.success('更新房源成功');
      } else {
        // 新增模式
        await createApartment(formData);
        message.success('创建房源成功');
      }

      onSuccess();
    } catch (error) {
      if (error.errorFields) {
        message.error('请完善表单信息');
      } else {
        message.error(error.message || (record ? '更新房源失败' : '创建房源失败'));
      }
    } finally {
      setLoading(false);
    }
  };

  // 图片上传前的验证
  const beforeImageUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件！');
      return false;
    }
    const isLt50M = file.size / 1024 / 1024 < 50;
    if (!isLt50M) {
      message.error('图片大小不能超过 50MB！');
      return false;
    }
    return true; // 允许上传
  };

  // 视频上传前的验证
  const beforeVideoUpload = (file) => {
    const isVideo = file.type.startsWith('video/');
    if (!isVideo) {
      message.error('只能上传视频文件！');
      return false;
    }
    const isLt50M = file.size / 1024 / 1024 < 50;
    if (!isLt50M) {
      message.error('视频大小不能超过 50MB！');
      return false;
    }
    return true; // 允许上传
  };

  // 自定义图片上传
  const handleImageUpload = async (index, { file, onSuccess, onError }) => {
    try {
      const result = await uploadFile(file);
      if (result.code === 200 && result.data?.url) {
        // 上传成功，保存URL
        onSuccess(result.data.url);
      } else {
        onError(new Error(result.msg || '上传失败'));
      }
    } catch (error) {
      onError(error);
    }
  };

  // 自定义视频上传
  const handleVideoUpload = async (index, { file, onSuccess, onError }) => {
    try {
      const result = await uploadFile(file);
      if (result.code === 200 && result.data?.url) {
        // 上传成功，保存URL
        onSuccess(result.data.url);
        message.success('视频上传成功');
      } else {
        onError(new Error(result.msg || '上传失败'));
        message.error(result.msg || '视频上传失败');
      }
    } catch (error) {
      onError(error);
      message.error(error.message || '视频上传失败');
    }
  };

  // 处理房型图片变化
  const handleRoomTypeImageChange = (index, { fileList }) => {
    const newFileLists = [...roomTypeFileLists];
    if (!newFileLists[index]) {
      newFileLists[index] = { images: [], video: [] };
    }
    // 保存文件列表，每个文件对象包含 url（来自后端）或 response（上传响应）
    newFileLists[index].images = fileList.slice(0, 9); // 最多9张
    setRoomTypeFileLists(newFileLists);
    
    // 批量上传进度提示
    const uploadingCount = fileList.filter(f => f.status === 'uploading').length;
    const doneCount = fileList.filter(f => f.status === 'done').length;
    const errorCount = fileList.filter(f => f.status === 'error').length;
    const totalCount = doneCount + uploadingCount + errorCount;
    
    // 使用房型索引作为 key，避免多个房型的消息冲突
    const messageKey = `upload-room-${index}`;
    
    if (uploadingCount > 0) {
      // 正在上传
      if (!uploadingMessagesRef.current[messageKey]) {
        uploadingMessagesRef.current[messageKey] = true;
      }
      message.loading({
        content: `正在上传图片 ${doneCount}/${totalCount}...`,
        key: messageKey,
        duration: 0
      });
    } else if (totalCount > 0) {
      // 上传完成
      if (uploadingMessagesRef.current[messageKey]) {
        message.destroy(messageKey);
        delete uploadingMessagesRef.current[messageKey];
        
        if (errorCount > 0) {
          message.warning(`已上传 ${doneCount} 张图片，${errorCount} 张失败`);
        } else if (doneCount > 1) {
          message.success(`成功上传 ${doneCount} 张图片`);
        } else if (doneCount === 1) {
          message.success('图片上传成功');
        }
      }
    }
  };

  // 处理图片拖拽排序
  const handleImageDragEnd = (index, dragIndex, hoverIndex) => {
    const newFileLists = [...roomTypeFileLists];
    if (!newFileLists[index] || !newFileLists[index].images) return;
    
    const images = [...newFileLists[index].images];
    const dragItem = images[dragIndex];
    
    // 移除拖拽项
    images.splice(dragIndex, 1);
    // 插入到新位置
    images.splice(hoverIndex, 0, dragItem);
    
    newFileLists[index].images = images;
    setRoomTypeFileLists(newFileLists);
  };

  // 处理房型视频变化
  const handleRoomTypeVideoChange = (index, { fileList }) => {
    const newFileLists = [...roomTypeFileLists];
    if (!newFileLists[index]) {
      newFileLists[index] = { images: [], video: [] };
    }
    // 保存文件列表
    newFileLists[index].video = fileList.slice(0, 1); // 最多1个
    setRoomTypeFileLists(newFileLists);
  };

  // 处理地址搜索（实时搜索）
  const handleAddressSearch = (value) => {
    if (!value || !autoComplete) {
      setAddressOptions([]);
      return;
    }
    
    autoComplete.search(value, (status, result) => {
      if (status === 'complete' && result.tips) {
        // 转换为 AutoComplete 需要的格式
        const options = result.tips
          .filter(tip => tip.location) // 只保留有经纬度的结果
          .map(tip => ({
            value: tip.name,
            label: tip.name + (tip.address ? ` (${tip.address})` : ''),
            location: tip.location, // 保存经纬度对象
            amapId: tip.id, // 保存地址的ID
            tip: tip // 保存完整的 tip 对象
          }));
        setAddressOptions(options);
      } else {
        setAddressOptions([]);
      }
    });
  };

  // 处理地址选择
  const handleAddressSelect = (value, option) => {
    if (option && option.location) {
      // 将经纬度转换为字符串格式保存
      const coordinate = `${option.location.lng},${option.location.lat}`;
      
      // 自动填充地址、坐标、amapId 和 mapName
      form.setFieldsValue({
        address: value,
        coordinate: coordinate,
        amapId: option.amapId, // 保存地址ID
        mapName: value // 保存地址名称
      });
      
      message.success('已自动填充地址和坐标');
    }
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>上传</div>
    </div>
  );

  return (
    <Modal
      title={detailOnly ? '房源详情' : (record ? '编辑房源' : '上传新房源')}
      open={visible}
      onCancel={onCancel}
      onOk={detailOnly ? undefined : handleSubmit}
      confirmLoading={loading}
      width={1000}
      okText={record ? '更新' : '上传'}
      cancelText="取消"
      footer={detailOnly ? null : undefined}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        className="apartment-form"
        disabled={detailOnly}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="公寓名称"
              rules={[{ required: true, message: '请输入公寓名称' }]}
            >
              <Input placeholder="请输入公寓名称" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="district"
              label="行政区"
              rules={[{ required: true, message: '请选择行政区' }]}
            >
              <Select 
                placeholder="请选择行政区"
                showSearch
                optionFilterProp="children"
              >
                {districtOptions.map(item => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="subway"
              label="地铁站"
              rules={[{ required: true, message: '请选择地铁站' }]}
            >
              <Cascader
                options={subwayOptions}
                placeholder="请选择地铁线路和站点"
                showSearch
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 隐藏字段：联系人手机号（默认为当前用户手机号）*/}
        <Form.Item
          name="contactPhone"
          hidden
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="services"
          label="公寓服务"
        >
          <Checkbox.Group options={serviceOptions} />
        </Form.Item>

        <Form.Item
          name="hasKitchen"
          label="是否有厨房"
          rules={[{ required: true, message: '请选择是否有厨房' }]}
        >
          <Radio.Group>
            {kitchenOptions.map(item => (
              <Radio key={item.value} value={item.value}>
                {item.label}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="facilities"
          label="家具设施"
        >
          <Checkbox.Group options={facilityOptions} />
        </Form.Item>

        <Form.Item
          name="description"
          label="公寓描述"
          rules={[
            { required: true, message: '请输入公寓描述' },
            { max: 200, message: '描述不能超过200字' }
          ]}
        >
          <TextArea
            rows={4}
            placeholder="请输入公寓描述（最多200字）"
            showCount
            maxLength={200}
          />
        </Form.Item>

        <Divider>地理位置</Divider>

        <Form.Item
          name="address"
          label="地址搜索"
          tooltip="输入地址关键词搜索，选择地址后自动填充坐标信息"
        >
          <AutoComplete
            options={addressOptions}
            onSearch={handleAddressSearch}
            onSelect={handleAddressSelect}
            placeholder="请输入地址关键词（如：上海市浦东新区世纪大道1号）"
            allowClear
          />
        </Form.Item>

        <Form.Item
          name="coordinate"
          label="坐标"
          rules={[{ required: true, message: '请先搜索并选择地址' }]}
          tooltip="格式：经度,纬度"
        >
          <Input
            placeholder="选择地址后自动填充（格式：经度,纬度）"
            disabled
          />
        </Form.Item>

        {/* 隐藏字段：保存地址ID */}
        <Form.Item
          name="amapId"
          hidden
          rules={[{ required: true, message: '请先搜索并选择地址' }]}
        >
          <Input />
        </Form.Item>

        {/* 隐藏字段：保存地址名称 */}
        <Form.Item
          name="mapName"
          hidden
          rules={[{ required: true, message: '请先搜索并选择地址' }]}
        >
          <Input />
        </Form.Item>

        <Divider>房型信息</Divider>

        <Form.List name="roomTypes">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => {
                const fileList = roomTypeFileLists[index] || { images: [], video: [] };
                
                return (
                  <Card
                    key={field.key}
                    title={`房型 ${index + 1}`}
                    extra={
                      !detailOnly && fields.length > 1 ? (
                        <Button
                          type="link"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => {
                            remove(field.name);
                            // 同时删除对应的文件列表
                            const newFileLists = [...roomTypeFileLists];
                            newFileLists.splice(index, 1);
                            setRoomTypeFileLists(newFileLists);
                          }}
                        >
                          删除房型
                        </Button>
                      ) : null
                    }
                    style={{ marginBottom: 16 }}
                  >
                    <Form.Item
                      {...field}
                      name={[field.name, 'typeName']}
                      label="房型名称"
                      rules={[{ required: true, message: '请输入房型名称' }]}
                    >
                      <Input placeholder="请输入房型名称（如：主卧、次卧、单间等）" />
                    </Form.Item>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'roomType']}
                          label="户型"
                          rules={[{ required: true, message: '请选择户型' }]}
                        >
                          <Select placeholder="请选择户型">
                            {roomTypeOptions.map(item => (
                              <Option key={item.value} value={item.value}>
                                {item.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'roomArea']}
                          label="面积（㎡）"
                          rules={[{ required: true, message: '请输入面积' }]}
                        >
                          <InputNumber
                            min={0}
                            style={{ width: '100%' }}
                            placeholder="请输入面积"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'price']}
                          label="价格（元/月）"
                          rules={[{ required: true, message: '请输入价格' }]}
                        >
                          <InputNumber
                            min={0}
                            style={{ width: '100%' }}
                            placeholder="请输入价格"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'orientation']}
                          label="朝向"
                          rules={[{ required: true, message: '请选择朝向' }]}
                        >
                          <Select placeholder="请选择朝向">
                            {orientationOptions.map(item => (
                              <Option key={item.value} value={item.value}>
                                {item.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      label="图片"
                      required={!detailOnly}
                      tooltip={detailOnly ? undefined : "最多上传9张图片，支持批量上传和拖拽排序，单个文件不超过50MB"}
                    >
                      {detailOnly ? (
                        <div className="apartment-modal-readonly-images">
                          {fileList.images && fileList.images.length > 0 ? (
                            fileList.images.map((file, i) => (
                              <img
                                key={file.uid || i}
                                src={file.url || file.response}
                                alt={`${index + 1}-${i}`}
                                style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }}
                              />
                            ))
                          ) : (
                            <span style={{ color: '#999' }}>暂无图片</span>
                          )}
                        </div>
                      ) : (
                        <>
                          <Upload
                            listType="picture-card"
                            fileList={fileList.images}
                            beforeUpload={beforeImageUpload}
                            customRequest={(options) => handleImageUpload(index, options)}
                            onChange={(info) => handleRoomTypeImageChange(index, info)}
                            maxCount={9}
                            multiple
                            itemRender={(originNode, file, currFileList) => {
                              const currentIndex = currFileList.findIndex(item => item.uid === file.uid);
                              return (
                                <div
                                  draggable
                                  onDragStart={(e) => {
                                    e.dataTransfer.effectAllowed = 'move';
                                    e.dataTransfer.setData('dragIndex', currentIndex);
                                  }}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = 'move';
                                  }}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    const dragIndex = parseInt(e.dataTransfer.getData('dragIndex'));
                                    if (dragIndex !== currentIndex) {
                                      handleImageDragEnd(index, dragIndex, currentIndex);
                                    }
                                  }}
                                  style={{ cursor: 'move' }}
                                >
                                  {originNode}
                                </div>
                              );
                            }}
                          >
                            {fileList.images.length >= 9 ? null : uploadButton}
                          </Upload>
                          <div style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
                            支持批量上传，单个文件不超过 50MB
                          </div>
                        </>
                      )}
                    </Form.Item>

                    <Form.Item
                      label="视频"
                      tooltip={detailOnly ? undefined : "最多上传1个视频，文件不超过50MB"}
                    >
                      {detailOnly ? (
                        <div className="apartment-modal-readonly-video">
                          {fileList.video && fileList.video.length > 0 && (fileList.video[0].url || fileList.video[0].response) ? (
                            <video
                              src={fileList.video[0].url || fileList.video[0].response}
                              controls
                              style={{ maxWidth: 200, maxHeight: 150 }}
                            />
                          ) : (
                            <span style={{ color: '#999' }}>暂无视频</span>
                          )}
                        </div>
                      ) : (
                        <>
                          <Upload
                            listType="picture-card"
                            fileList={fileList.video}
                            beforeUpload={beforeVideoUpload}
                            customRequest={(options) => handleVideoUpload(index, options)}
                            onChange={(info) => handleRoomTypeVideoChange(index, info)}
                            maxCount={1}
                          >
                            {fileList.video.length >= 1 ? null : uploadButton}
                          </Upload>
                          <div style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
                            支持常见视频格式，文件不超过 50MB
                          </div>
                        </>
                      )}
                    </Form.Item>
                  </Card>
                );
              })}
              
              {!detailOnly && (
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => {
                      add();
                      // 同时添加对应的文件列表
                      setRoomTypeFileLists([...roomTypeFileLists, { images: [], video: [] }]);
                    }}
                    block
                    icon={<PlusOutlined />}
                  >
                    添加房型
                  </Button>
                </Form.Item>
              )}
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default ApartmentModal;



